import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Bot,
  Layers,
  Rocket,
  FlaskConical,
  HeartHandshake,
  Briefcase,
  Medal,
  Mic,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import Monogram from './layout/Monogram';
import Stamp from './layout/Stamp';

interface Mentor {
  id: string;
  displayName: string;
  bio: string;
  university: string | null;
  major: string | null;
  classYear: string | null;
  mentorType?: string;
  occupation?: string | null;
  almaMater?: string | null;
  pricePerSession: number;
  isVerified: boolean;
  isTopRated: boolean;
  totalSessions: number;
  averageRating: number | null;
  achievements: Array<{
    id: string;
    category: string;
    title: string;
    icon: string | null;
    isPrimary: boolean;
    role?: string | null;
    childName?: string | null;
    verificationStatus?: string;
  }>;
  specialties: Array<{
    id: string;
    title: string;
    category: string;
  }>;
  admissions: Array<{
    id: string;
    university: string;
    isAttending: boolean;
    year?: number | null;
    childName?: string | null;
  }>;
  reviews: Array<{
    id: string;
    rating: number;
    content: string;
  }>;
}

const POPULAR_ACTIVITIES: Array<{
  label: string;
  filter: string;
  Icon: React.ComponentType<{ size?: number | string }>;
}> = [
  { label: 'Robotics competitions', filter: 'robotics', Icon: Bot },
  { label: 'Top college activity portfolio', filter: 'portfolio', Icon: Layers },
  { label: 'Start a business/organization', filter: 'entrepreneurship', Icon: Rocket },
  { label: 'Research project', filter: 'research', Icon: FlaskConical },
  { label: 'Social impact project', filter: 'social impact', Icon: HeartHandshake },
  { label: 'Business competitions', filter: 'business competition', Icon: Briefcase },
  { label: 'Olympiad', filter: 'olympiad', Icon: Medal },
  { label: 'Debate & speech', filter: 'debate & speech', Icon: Mic },
  { label: 'Highly gifted children', filter: 'gifted', Icon: Sparkles },
];

const MentorMarketplace: React.FC = () => {
  const location = useLocation();
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });
  const [universityFilter, setUniversityFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState(''); // popular activity tile
  const [subActivityFilter, setSubActivityFilter] = useState(''); // chip within the tile
  const [activityTierFilter, setActivityTierFilter] = useState('');
  const [mentorTab, setMentorTab] = useState<'student' | 'parent'>(
    location.hash === '#parents' ? 'parent' : 'student'
  );
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [waitlistJoining, setWaitlistJoining] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [activities, setActivities] = useState<Array<{ id: string; name: string; category: string }>>([]);

  useEffect(() => {
    apiService.getActivities().then((r) => setActivities(r.activities || []));
  }, []);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const achievementFilter = subActivityFilter || activityFilter;

  const fetchMentors = useCallback(async () => {
    try {
      const params: any = { mentorType: mentorTab };
      if (universityFilter) params.university = universityFilter;
      if (searchQuery) params.search = searchQuery;
      if (achievementFilter) params.achievement = achievementFilter;
      if (activityTierFilter) params.activityTier = activityTierFilter;

      const response = await axios.get(`${API_BASE_URL}/mentors`, { params });
      setMentors(response.data.data || response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching mentors:', error);
      setLoading(false);
    }
  }, [API_BASE_URL, mentorTab, universityFilter, searchQuery, achievementFilter, activityTierFilter]);

  useEffect(() => {
    fetchMentors();
  }, [fetchMentors]);

  useEffect(() => {
    if (location.hash === '#parents') setMentorTab('parent');
  }, [location.hash]);

  const handleTabSwitch = (tab: 'student' | 'parent') => {
    if (tab === mentorTab) return;
    setMentorTab(tab);
    setSearchQuery('');
    setUniversityFilter('');
    setActivityFilter('');
    setSubActivityFilter('');
    setActivityTierFilter('');
  };

  const togglePopularActivity = (filter: string) => {
    const next = activityFilter === filter ? '' : filter;
    setActivityFilter(next);
    setSubActivityFilter('');
    // School filter is only visible for the portfolio tile — clear it when hidden
    if (next !== 'portfolio') setUniversityFilter('');
  };

  const toggleSubActivity = (chip: string) => {
    setSubActivityFilter((prev) => (prev === chip ? '' : chip));
  };

  // Chips shown in the sidebar: scoped to the selected popular activity
  const visibleChips = activityFilter
    ? activities.filter((a) => a.category.toLowerCase() === activityFilter).map((a) => a.name)
    : activities.map((a) => a.name);

  const activityRowRef = useRef<HTMLDivElement>(null);

  const scrollActivities = (dir: 1 | -1) => {
    const row = activityRowRef.current;
    if (row) row.scrollBy({ left: dir * row.clientWidth, behavior: 'smooth' });
  };

  const handleRequestMentor = async () => {
    if (!requestEmail || !requestEmail.includes('@')) return;
    if (waitlistJoining) return;
    setWaitlistJoining(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/waitlist/request`, {
        email: requestEmail,
        segment: `${searchQuery || achievementFilter || 'Any activity'} · ${mentorTab}`,
        mentorType: mentorTab,
        note: requestNote.trim() || searchQuery || null,
      });
      if (response.data.success) {
        setWaitlistJoined(true);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
    }
    setWaitlistJoining(false);
  };

  const getCred = (mentor: Mentor): string => {
    if (mentor.mentorType === 'parent') return `${mentor.displayName} — parent mentor`;
    const school = [mentor.university, mentor.classYear ? `'${String(mentor.classYear).slice(-2)}` : null]
      .filter(Boolean)
      .join(' ');
    return school ? `${mentor.displayName} — ${school}` : mentor.displayName;
  };

  const getMeta = (mentor: Mentor): string => {
    if (mentor.mentorType === 'parent') {
      return [mentor.occupation, mentor.almaMater ? `${mentor.almaMater} alum` : null]
        .filter(Boolean)
        .join(' · ') || 'Veteran competition parent';
    }
    const primary = mentor.achievements?.find((a) => a.isPrimary) || mentor.achievements?.[0];
    return (
      [primary?.title, primary?.role, mentor.major].filter(Boolean).join(' · ') ||
      mentor.major ||
      ''
    );
  };

  const renderCard = (mentor: Mentor) => (
    <div key={mentor.id} className="mentor-card">
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <Monogram name={mentor.displayName} />
        <div>
          <div className="cred">{getCred(mentor)}</div>
          <div className="meta">{getMeta(mentor)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {mentor.mentorType === 'parent' ? (
          <>
            {mentor.isVerified && <Stamp variant="verified">Cost record on file</Stamp>}
            <span className="tag">Parent</span>
          </>
        ) : (
          <>
            {mentor.achievements?.map((achievement) => (
              <span
                key={achievement.id}
                className={`tag${achievement.verificationStatus === 'verified' ? ' verified' : ''}`}
              >
                {achievement.verificationStatus === 'verified' && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="#22863a"
                    style={{ marginRight: 4, verticalAlign: '-2px' }}
                  >
                    <circle cx="12" cy="12" r="12" fill="#22863a" />
                    <path
                      d="M9.5 15.5L6 12l1.5-1.5 2 2 5-5L16 9l-6.5 6.5z"
                      fill="white"
                    />
                  </svg>
                )}
                {achievement.title || achievement.category}
              </span>
            ))}
          </>
        )}
      </div>
      {mentor.bio && (
        <p className="meta">
          “{mentor.bio.length > 140 ? `${mentor.bio.slice(0, 140)}…` : mentor.bio}”
        </p>
      )}
      <div className="price">
        <b>${mentor.pricePerSession}</b> / hour
        {mentor.totalSessions > 0 ? ` · ${mentor.totalSessions} sessions` : ''}
        {mentor.averageRating ? ` · ${mentor.averageRating.toFixed(1)} ★` : ''}
      </div>
      <Link className="btn btn-secondary btn-sm" to={`/mentors/${mentor.id}`}>
        View profile
      </Link>
    </div>
  );

  return (
    <>
      <header className="section" style={{ padding: '56px 0 0' }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Verified mentors · robotics &amp; USACO
          </span>
          <h1 style={{ fontSize: 34 }}>Browse mentors</h1>

          <div className="browse-search" style={{ maxWidth: 600, marginTop: 24 }}>
            <div className="home-search" style={{ boxShadow: 'none' }}>
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9A857D"
                strokeWidth="2"
                strokeLinecap="round"
                style={{ flex: 'none' }}
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M16.5 16.5 21 21" />
              </svg>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by activity, school, award, or keyword..."
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', padding: '12px 0', fontSize: '16px' }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{ background: 'none', border: 'none', color: 'var(--ink-faint)', cursor: 'pointer', padding: '8px' }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="tabs" style={{ marginTop: 24 }}>
            <button
              className={mentorTab === 'student' ? 'on' : ''}
              onClick={() => handleTabSwitch('student')}
            >
              Student mentors{' '}
              {mentorTab === 'student' && !loading && <span className="meta">({mentors.length})</span>}
            </button>
            <button
              className={mentorTab === 'parent' ? 'on' : ''}
              onClick={() => handleTabSwitch('parent')}
            >
              Parent mentors{' '}
              {mentorTab === 'parent' && !loading && <span className="meta">({mentors.length})</span>}
            </button>
          </div>
        </div>
      </header>

      <section className="activity-strip">
        <div className="wrap">
          <div className="activity-strip-head">
            <h2>Browse by most popular activities</h2>
            <div className="activity-arrows">
              <button className="arrow-btn" aria-label="Scroll left" onClick={() => scrollActivities(-1)}>
                <ChevronLeft size={16} />
              </button>
              <button className="arrow-btn" aria-label="Scroll right" onClick={() => scrollActivities(1)}>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          <div className="activity-row" ref={activityRowRef}>
            {POPULAR_ACTIVITIES.map(({ label, filter, Icon }) => (
              <button
                key={filter}
                className={`activity-tile${activityFilter === filter ? ' on' : ''}`}
                onClick={() => togglePopularActivity(filter)}
              >
                <span className="activity-label">{label}</span>
                <span className="activity-icon">
                  <Icon size={20} />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <main className="section" style={{ paddingTop: 32 }}>
        <div
          className="wrap"
          style={{
            display: 'grid',
            gridTemplateColumns: '240px 1fr',
            gap: 40,
            alignItems: 'start',
          }}
        >
          <aside>
            <div className="field">
              <label htmlFor="f-search">Search</label>
              <input
                id="f-search"
                type="search"
                placeholder="Team number, school, award…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {visibleChips.length > 0 && (
              <div className="field">
                <label>Activity</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {visibleChips.map((chip) => (
                    <button
                      key={chip}
                      className={`chip${subActivityFilter === chip ? ' on' : ''}`}
                      onClick={() => toggleSubActivity(chip)}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activityFilter === 'portfolio' && (
              <div className="field">
                <label htmlFor="f-school">School</label>
                <select
                  id="f-school"
                  value={universityFilter}
                  onChange={(e) => setUniversityFilter(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="Harvard">Harvard</option>
                  <option value="MIT">MIT</option>
                  <option value="Stanford">Stanford</option>
                  <option value="Yale">Yale</option>
                  <option value="Princeton">Princeton</option>
                  <option value="UC Berkeley">UC Berkeley</option>
                </select>
              </div>
            )}
            <div className="field">
              <label htmlFor="f-region">Region</label>
              <select id="f-region">
                <option>Any</option>
                <option>Pacific Northwest</option>
                <option>California</option>
                <option>Texas</option>
                <option>Northeast</option>
                <option>Midwest</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="f-activity-tier">Activity tier</label>
              <select
                id="f-activity-tier"
                value={activityTierFilter}
                onChange={(e) => setActivityTierFilter(e.target.value)}
              >
                <option value="">Any</option>
                <option value="state">State ranking</option>
                <option value="championship">National/international ranking (championship)</option>
                <option value="top300">Top 300</option>
              </select>
            </div>
            <div className="note" style={{ marginBottom: 12 }}>
              Rates follow the verified record: world-level $150–200 · national $100–150 ·
              state/regional $75–95. Every rate is tied to a checkable credential.
            </div>
            <div className="note">
              Can't find an exact match?{' '}
              <Link to="/family-dashboard" style={{ textDecoration: 'underline' }}>
                Join the waitlist
              </Link>{' '}
              — demand drives who we recruit next.
            </div>
          </aside>

          <div>
            {loading ? (
              <p className="meta">Loading mentors…</p>
            ) : mentors.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '80px 40px',
                  border: '1px dashed var(--hairline-strong)',
                  borderRadius: 16,
                  background: 'var(--warm)',
                }}
              >
                <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 28, marginBottom: 12 }}>
                  No mentors found
                </h2>
                <p style={{ color: 'var(--ink-secondary)', maxWidth: 400, margin: '0 auto 24px' }}>
                  We don't have an exact match yet, but tell us what you're looking for and we'll
                  contact you as soon as we find a good match.
                </p>
                {waitlistJoined ? (
                  <div className="note" style={{ display: 'inline-block' }}>
                    ✓ Request submitted — we'll email you when we find a match.
                  </div>
                ) : (
                  <div style={{ maxWidth: 420, margin: '0 auto', textAlign: 'left' }}>
                    <textarea
                      placeholder="What kind of mentor are you looking for? e.g. activity, competition level, goals…"
                      value={requestNote}
                      onChange={(e) => setRequestNote(e.target.value)}
                      rows={3}
                      style={{ width: '100%', resize: 'vertical', marginBottom: 8 }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="email"
                        placeholder="Your email address"
                        value={requestEmail}
                        onChange={(e) => setRequestEmail(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={handleRequestMentor}
                        disabled={waitlistJoining || !requestEmail.includes('@')}
                      >
                        {waitlistJoining ? 'Submitting…' : 'Request a mentor'}
                      </button>
                    </div>
                    <div className="meta" style={{ marginTop: 8 }}>
                      Your request goes straight to our team — we recruit new mentors based on what
                      families ask for.
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {mentors.map(renderCard)}
                {mentorTab === 'student' ? (
                  <div
                    className="mentor-card"
                    style={{
                      borderStyle: 'dashed',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                    }}
                  >
                    <h3>Your exact match may be next</h3>
                    <p className="meta">
                      We recruit against demand — join the waitlist and we'll notify you when your
                      match goes live.
                    </p>
                    {waitlistJoined ? (
                      <div className="note">
                        You're on the list — we'll email you when a matching mentor goes live.
                      </div>
                    ) : (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleRequestMentor}
                        disabled={waitlistJoining}
                      >
                        {waitlistJoining ? 'Joining…' : 'Join the waitlist'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    className="mentor-card"
                    style={{
                      borderStyle: 'dashed',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                    }}
                  >
                    <h3>Veteran parents: share what you learned</h3>
                    <p className="meta">
                      Honorarium, status, and a community of families one step behind you. No one
                      else offers this session anywhere.
                    </p>
                    <Link className="btn btn-secondary btn-sm" to="/become-a-mentor">
                      Become a parent mentor
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default MentorMarketplace;
